import {
  computeHaloSeconds,
  containsWindowedCall,
  evaluate,
  evaluateAsSeries,
} from "./evaluator";
import { Parser, type ASTNode } from "./parser";
import { Tokenizer } from "./tokenizer";
import { validateAst } from "./validate";

/**
 * A math expression parsed and validated once, evaluable many times.
 *
 * Supported surface: point-wise math (`+ - * / % ^`, `abs/sqrt/pow/sin/cos/
 * tan/log/exp/min/max/round/floor/ceil/rad/deg`, `PI`/`E`, the variable `y`)
 * plus windowed functions over a series — `savgol(y, window, order)`
 * (Savitzky-Golay smoothing, `window` in seconds) and `deriv(y, n?)`
 * (central-difference derivative, `n` ∈ {1, 2}, defaults to 1). Windowed
 * calls compose with point-wise math.
 *
 * The `kind` tag selects the API: a `"scalar"` formula exposes `evaluatePoint`;
 * a `"windowed"` one exposes `evaluateSeries` and `requiredHaloSeconds`, and no
 * `evaluatePoint`. Branch on `kind`; TypeScript then narrows to that arm.
 */
export type CompiledFormula =
  | {
      readonly kind: "scalar";
      /** Evaluate against a single `y`. */
      evaluatePoint(y: number): number;
    }
  | {
      readonly kind: "windowed";
      /**
       * Evaluate over a buffered series. `timeSeconds` must align 1:1 with
       * `values`, since kernels resolve their window in seconds. Result length
       * matches the input.
       */
      evaluateSeries(values: number[], timeSeconds: number[]): number[];
      /**
       * Seconds of neighbouring samples this formula's kernels need on each side
       * of a point to compute its output, at the topic's nominal
       * `sampleIntervalSeconds`. A caller loading a sub-range (e.g. a zoomed
       * viewport) should extend the read by this much on both sides so edge
       * values match a full-series evaluation.
       */
      requiredHaloSeconds(sampleIntervalSeconds: number): number;
    };

/**
 * Compile a math expression into a reusable {@link CompiledFormula}.
 *
 * Throws on parse errors, unknown identifiers/functions, and malformed windowed
 * calls (wrong arity, non-constant window/order/n arguments, out-of-range
 * values).
 *
 * @example
 *   const f = compileFormula("y * 100 + 1");
 *   if (f.kind === "scalar") f.evaluatePoint(2);  // 201
 *
 *   const g = compileFormula("deriv(savgol(y, 0.5, 3))");
 *   if (g.kind === "windowed") g.evaluateSeries(values, timeSeconds);
 */
export function compileFormula(expr: string): CompiledFormula {
  let ast: ASTNode;
  try {
    const tokens = new Tokenizer(expr).tokenize();
    ast = Parser.parse(tokens);
  } catch (e) {
    // The tokenizer/parser throw on syntax errors (unlike validateAst, which
    // returns a result). Normalise any non-Error throw so every failure out of
    // compileFormula is an Error, as the docstring promises.
    throw e instanceof Error ? e : new Error(String(e));
  }
  const validation = validateAst(ast);
  if (!validation.ok) {
    throw new Error(validation.error);
  }
  if (containsWindowedCall(ast)) {
    return {
      kind: "windowed",
      evaluateSeries(values: number[], timeSeconds: number[]): number[] {
        return evaluateAsSeries(ast, values, timeSeconds, validation.constants);
      },
      requiredHaloSeconds(sampleIntervalSeconds: number): number {
        return computeHaloSeconds(validation.constants, sampleIntervalSeconds);
      },
    };
  }
  return {
    kind: "scalar",
    evaluatePoint(y: number): number {
      return evaluate(ast, { y });
    },
  };
}

/**
 * Validate a math expression string without throwing.
 *
 * Returns `{ valid: true }` if the expression compiles successfully,
 * or `{ valid: false, error: string }` if it does not.
 */
export function validateFormula(
  expr: string,
): { valid: true } | { valid: false; error: string } {
  try {
    compileFormula(expr);
    return { valid: true };
  } catch (e) {
    return { valid: false, error: (e as Error).message };
  }
}
